/**
 * 用于控制全局的 login 弹窗的显示隐藏
 * isLogin：用户是否登录
 * dialogState：弹窗状态
 * -- confirm 让用户确认是否登录的弹窗
 * -- login 用户登录的弹窗
 * -- real-name 用户实名认证的弹窗
 * -- one-click-login：一键登录弹窗
 * loginPointParams：登录弹窗中埋点的数据
 * eventMap：事件Map，用于模拟登录的整个生命周期
 * -- cancelLogin 取消登录
 * -- afterLogin 登录之后
 * -- isAgentPerson 个人和合伙人
 */

import { defineStore } from 'pinia';
import { storage } from '@/utils/storage';
import { getUserDetail } from '@/api/basic';
import router from '@/router';

export type DialogState = 'confirm' | 'login' | 'real-name' | 'one-click-login' | '';
export type EventName = 'beforeLogin' | 'afterLogin' | 'cancelLogin';
export type PointName = 'afterLogin' | 'cancelLogin' | 'visible';
export type UserInfo = {
  phone: string;
  id: number;
  headImgUrl: string;
  nickName: string;
  agentPhone?: string;
  agentName?: string;
  isAgent: 0 | 1; // 是否为代理人 1是 0否
  reviewStatus: 0 | 1 | 2; // 审核状态 0：未审核 1：审核通过 2：审核不通过
  showInviteSubordinates: 0 | 1; // 是否显示邀请下级代理人
  balance: number; // 钱包总余额(分)
  balanceBd: number; // 钱包总余额(元)
  isRealName: 0 | 1; // 是否实名
  paths?: null;
  isBuyPromote?: string | number; //是否查询过报告
  isSignYzhAgreement?: string | number; //云账户签约状态 0：未签约 1：已签约
  vipDetailVO?: VipInfo; // vip信息
  realName?: string; // 昵称
};
export type VipInfo = {
  vipProductId?: number;
  name?: string;
  pageUsed?: number;
  vipStatus?: number;
  effectiveStartTime?: number;
  effectiveEndTime?: number;
  vipIncome?: number;
};
export type CheckIsLoginParams = {
  showModal: boolean;
  getUserInfo: boolean;
  freshUserInfo?: boolean;
};
export type urlParamsType = {
  businessId: string;
  productId: string;
  availableDateDay: string;
  availableDateMinute: string;
  page: string;
  pId: string;
  channelId: string;
  tenantId: string;
  shareBorrowerType: string;
  channelPromotionTaskType: string;
  oneCustomerOneCode: string;
  sign: string;
  userId: string;
};
export const useLoginDialog = defineStore('useLoginDialog', () => {
  const urlParams = ref<Partial<urlParamsType>>({}); // 留资页面url参数
  const isLogin = ref(false);
  const loginInfo = ref<Partial<UserInfo>>({});
  const setIsLogin = (state: boolean) => (isLogin.value = state);
  const setUrlParams = (state: any) => (urlParams.value = state);
  const getUserInfo = (refresh?: boolean) => {
    (!loginInfo.value.id || refresh) && getUserDetail().then((res) => (loginInfo.value = res));
  };
  const isAgentPerson = ref<any>(storage.getItem('isAgentPerson') || 0); // 0--C端 1--L端
  const isTokenTab = ref<any>(false); // token失效 true == 失效
  const setIsTokenTab = (type: boolean) => {
    isTokenTab.value = type;
  };
  const setIsAgentPerson = (type: number) => {
    isAgentPerson.value = type;
  };
  const dialogState = ref<DialogState>('');
  const checkIsLogin = (options: Partial<CheckIsLoginParams> = {}) => {
    const curOpt = Object.assign(
      {
        showModal: true,
        getUserInfo: false,
      } as CheckIsLoginParams,
      options
    );
    isLogin.value = storage.checkAuth();
    if (isLogin.value) {
      curOpt.getUserInfo && getUserInfo(curOpt.freshUserInfo);
    } else {
      curOpt.showModal && setLoginState('confirm');
      curOpt.getUserInfo && addEvent('afterLogin', () => getUserInfo(curOpt.freshUserInfo));
    }
    return isLogin.value;
  };
  const setLoginState = (state: DialogState) => (dialogState.value = state);

  // 登录接口需要的参数
  const loginParams = ref<Record<string, any>>({});
  const addLoginParams = (params: Record<string, any>) => (loginParams.value = params);
  const clearLoginParams = () => (loginParams.value = {});

  const loginPointParams = ref<Record<string, any>>({
    registerAgentFlag: 0,
  });
  const setPointParams = (data: Record<string, any>) => (loginPointParams.value = data);
  const loginPointDataMap = ref<Map<PointName, Record<string, any>>>(new Map());
  const addPointData = (pointName: PointName, data: Record<string, any>) =>
    loginPointDataMap.value.set(pointName, data);
  const getPointData = (pointName: PointName) => loginPointDataMap.value.get(pointName);
  const clearPointData = () => loginPointDataMap.value.clear();

  const closeDialog = () => {
    setLoginState('');
    setPointParams({});
    clearLoginParams();
    clearPointData();
    clearEvent();
  };

  // 事件相关--类似于登录的整个生命周期的钩子
  const eventMap = ref<Map<EventName, (() => void)[]>>(new Map());
  const addEvent = (eventName: EventName, handle: () => void) => {
    const curEvent = getEvent(eventName);
    if (curEvent) {
      curEvent.push(handle);
      eventMap.value.set(eventName, curEvent);
    } else {
      eventMap.value.set(eventName, [handle]);
    }
  };
  const getEvent = (eventName: EventName) => eventMap.value.get(eventName);
  const clearEvent = () => eventMap.value.clear();
  const executeEvent = (eventName: EventName) => {
    const curEvent = getEvent(eventName);
    curEvent && curEvent.length && curEvent.forEach((item) => item());
  };
  const executeEvents = (eventName: EventName | EventName[]) => {
    typeof eventName === 'string'
      ? executeEvent(eventName)
      : eventName.forEach((item) => executeEvent(item));
  };

  // 登录
  const toLogin = () => {
    setIsLogin(true);
    executeEvent('afterLogin');
    setTimeout(() => {
      window.location.reload();
    });
  };
  // 退出登录
  const toLoginout = (url?: any) => {
    storage.setItem('isAgentPerson', 0, null);
    closeDialog();
    isLogin.value = false;
    loginInfo.value = {};
    storage.removeItem('token');
    if (url) {
      router.push(url).then(() => {
        window.location.reload(); // 跳转完成后刷新
      });
    } else {
      setTimeout(() => {
        window.location.reload();
      });
    }
  };

  watch(dialogState, (newVal) => {
    newVal === '' && closeDialog();
  });

  return {
    isLogin,
    loginInfo,
    setIsLogin,
    getUserInfo,
    dialogState,
    checkIsLogin,
    setLoginState,

    loginParams,
    addLoginParams,
    clearLoginParams,

    loginPointParams,
    setPointParams,
    loginPointDataMap,
    addPointData,
    getPointData,
    clearPointData,

    closeDialog,

    eventMap,
    addEvent,
    getEvent,
    clearEvent,
    executeEvent,
    executeEvents,

    toLogin,
    toLoginout,
    isAgentPerson,
    setIsAgentPerson,
    isTokenTab,
    setIsTokenTab,
    urlParams,
    setUrlParams,
  };
});
